import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "../../../design-system";
import { fetchFilmReel, ReelClip } from "./filmReelShare";

/**
 * FlagshipTikTok — a self-contained "TikTok in a box".
 *
 * A phone-framed vertical reel that plays a user's picks as ONE continuous reel: a single
 * <video src={videoUrl}> seeks to each clip's [start, end], auto-advances at the end of each
 * clip, and loops back to the first when the reel finishes. It loads its clips lazily via
 * fetchFilmReel() (only once it nears the viewport) and only plays while on screen.
 *
 * Modeled on PhoneDemoPage's "feedPhone", but LEAN: one video element driven imperatively
 * (no per-clip <video>, no scroll-snap feed), minimal chrome, and no external dependencies
 * beyond the design-system + the shared film-reel loader. It edits no existing file.
 *
 * Caveats:
 *  - The presigned `videoUrl` and clip thumbs are cross-origin (S3). The <video> uses
 *    playsInline; a poster/thumb fallback covers an expired URL or a CORS/decode error.
 *  - Autoplay is only allowed while MUTED — the reel starts muted and exposes an unmute
 *    toggle. Unmuting after a user gesture is fine; unmuting programmatically is not.
 */

const CHERRY = "#ec2b57";
const F = "Outfit, system-ui, -apple-system, sans-serif";
const MONO = "'JetBrains Mono', ui-monospace, monospace";

type FlagshipTikTokProps = { heightPx?: number };

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

export const FlagshipTikTok: React.FC<FlagshipTikTokProps> = ({ heightPx = 610 }) => {
  const screenH = Math.max(360, heightPx);
  const screenW = Math.round(screenH * (288 / 610)); // keep PhoneDemoPage's phone proportions

  const [near, setNear] = useState(false); // approaching viewport -> allowed to fetch
  const [visible, setVisible] = useState(false); // actually on screen -> allowed to play
  const [clips, setClips] = useState<ReelClip[]>([]);
  const [videoUrl, setVideoUrl] = useState("");
  const [idx, setIdx] = useState(0);
  const [muted, setMuted] = useState(true);
  const [err, setErr] = useState(false);
  const [videoBroken, setVideoBroken] = useState(false);

  const rootRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const idxRef = useRef(0); // authoritative current index for race-free auto-advance

  const total = clips.length;
  const current = total ? clips[Math.min(idx, total - 1)] : undefined;

  /* ---- lazy-load trigger: fire once the phone nears the viewport ---- */
  useEffect(() => {
    const el = rootRef.current;
    if (!el || near) return;
    if (typeof IntersectionObserver === "undefined") { setNear(true); return; }
    const io = new IntersectionObserver((es) => {
      if (es.some((e) => e.isIntersecting)) { setNear(true); io.disconnect(); }
    }, { rootMargin: "300px" });
    io.observe(el);
    return () => io.disconnect();
  }, [near]);

  /* ---- play/pause gate: only play while genuinely on screen ---- */
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") { setVisible(true); return; }
    const io = new IntersectionObserver((es) => {
      es.forEach((e) => setVisible(e.isIntersecting));
    }, { threshold: 0.5 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  /* ---- fetch the reel once (lazily) ---- */
  useEffect(() => {
    if (!near || total || err) return;
    let cancelled = false;
    (async () => {
      try {
        const { videoUrl: url, clips: rows } = await fetchFilmReel();
        if (cancelled) return;
        const good = rows.filter((r) => r.end - r.start >= 1.5);
        setVideoUrl(url);
        setClips((good.length >= 3 ? good : rows).slice(0, 20));
      } catch (e) {
        if (!cancelled) setErr(true);
      }
    })();
    return () => { cancelled = true; };
  }, [near, total, err]);

  /* ---- keep the DOM `muted` in sync (React doesn't reliably reflect the prop) ---- */
  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted;
  }, [muted, videoUrl]);

  // Seek the single <video> to a clip's start, then play (once seeked / metadata-ready).
  const seekAndPlay = (t: number) => {
    const v = videoRef.current;
    if (!v) return;
    const run = () => {
      try {
        if (Math.abs(v.currentTime - t) > 0.25) {
          v.addEventListener("seeked", () => { v.play().catch(() => { /* autoplay denied */ }); }, { once: true });
          v.currentTime = t;
        } else {
          v.play().catch(() => { /* autoplay denied */ });
        }
      } catch (e) { /* ignore */ }
    };
    if (v.readyState >= 1) run();
    else {
      v.addEventListener("loadedmetadata", run, { once: true });
      try { v.load(); } catch (e) { /* ignore */ }
    }
  };

  /* ---- reset to the first clip whenever a fresh reel arrives ---- */
  useEffect(() => {
    idxRef.current = 0;
    setIdx(0);
  }, [videoUrl]);

  /* ---- drive playback from visibility: play current clip when on screen, pause off ---- */
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !total) return;
    if (visible) {
      const clip = clips[idxRef.current] || clips[0];
      const t = v.currentTime;
      if (Number.isNaN(t) || t < clip.start - 0.3 || t > clip.end + 0.3) seekAndPlay(clip.start);
      else v.play().catch(() => { /* autoplay denied */ });
    } else {
      try { v.pause(); } catch (e) { /* ignore */ }
    }
  }, [visible, videoUrl, total, clips]);

  // Advance to the next clip when the current one ends (loop back to the first at the end).
  const onTimeUpdate = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const v = e.currentTarget;
    const clip = clips[idxRef.current];
    if (!clip) return;
    if (progressRef.current) {
      const pct = clamp(((v.currentTime - clip.start) / Math.max(0.1, clip.end - clip.start)) * 100, 0, 100);
      progressRef.current.style.width = `${pct}%`;
    }
    if (v.currentTime >= clip.end - 0.05) {
      const next = total ? (idxRef.current + 1) % total : 0;
      idxRef.current = next;
      setIdx(next);
      seekAndPlay(clips[next].start);
    }
  };

  const toggleMute = () => {
    const next = !muted;
    if (videoRef.current) videoRef.current.muted = next;
    setMuted(next);
  };

  const hasVideo = !err && !!videoUrl && total > 0 && !videoBroken;

  /* ---- fallback message (interface voice, no apology) ---- */
  const fallbackMsg = err
    ? "This reel plays best in the full editor — open it to watch your picks roll."
    : videoBroken
      ? "Here's a still while the reel catches up — tap into the editor for the full roll."
      : "Cueing up your reel…";

  const showDots = total > 0 && total <= 12;

  return (
    <Box
      ref={rootRef}
      sx={{
        p: "10px", borderRadius: "44px", flexShrink: 0, width: "fit-content", mx: "auto",
        background: "linear-gradient(160deg,#2a2733,#141219)",
        border: "1px solid rgba(255,255,255,0.14)",
        boxShadow: `0 30px 80px rgba(0,0,0,0.6), 0 0 90px -30px ${CHERRY}66`,
      }}
    >
      <Box sx={{
        position: "relative", width: `${screenW}px`, height: `${screenH}px`,
        borderRadius: "34px", overflow: "hidden", background: "#08070b",
      }}>
        {/* notch */}
        <Box sx={{ position: "absolute", top: 7, left: "50%", ml: "-38px", width: 76, height: 20, borderRadius: "11px", background: "#000", border: "1px solid rgba(255,255,255,0.07)", zIndex: 7, pointerEvents: "none" }} />

        {/* the reel: one <video>, seeking each clip in turn */}
        {near && !err && videoUrl ? (
          <video
            ref={videoRef}
            src={videoUrl}
            poster={current?.thumb || undefined}
            muted={muted}
            playsInline
            preload="auto"
            onTimeUpdate={onTimeUpdate}
            onError={() => setVideoBroken(true)}
            onClick={(e) => {
              const v = e.currentTarget;
              if (v.paused) v.play().catch(() => { /* ignore */ });
              else v.pause();
            }}
            style={{
              position: "absolute", inset: 0, width: "100%", height: "100%",
              objectFit: "cover", cursor: "pointer",
              opacity: hasVideo ? 1 : 0,
            }}
          />
        ) : null}

        {/* poster / loading / error fallback */}
        {!hasVideo && (
          <Box sx={{ position: "absolute", inset: 0 }}>
            {current?.thumb ? (
              <img src={current.thumb} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.55 }} />
            ) : null}
            <Box sx={{
              position: "absolute", inset: 0, display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center", gap: "12px", px: "26px", textAlign: "center",
              background: current?.thumb ? "rgba(8,7,11,0.35)" : "radial-gradient(120% 120% at 50% 30%, #171320 0%, #08070b 70%)",
            }}>
              <Typography sx={{ fontFamily: MONO, fontSize: "11px", letterSpacing: "2px", color: CHERRY }}>CHERRYCLIPZ</Typography>
              <Typography sx={{ fontFamily: F, fontSize: "13px", lineHeight: 1.55, color: "#c2c7d6" }}>
                {fallbackMsg}
              </Typography>
            </Box>
          </Box>
        )}

        {/* readability scrim */}
        <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none", background: "linear-gradient(180deg, rgba(8,7,11,0.4) 0%, transparent 26%, transparent 52%, rgba(8,7,11,0.78) 100%)" }} />

        {/* current-clip progress bar */}
        {hasVideo && (
          <Box sx={{ position: "absolute", top: 34, left: 14, right: 14, height: 2, borderRadius: "999px", background: "rgba(255,255,255,0.16)", overflow: "hidden", zIndex: 5 }}>
            <Box ref={progressRef} sx={{ height: "100%", width: 0, background: CHERRY }} />
          </Box>
        )}

        {/* subtle "your picks, as one reel" label */}
        <Box sx={{
          position: "absolute", top: 44, left: 14, zIndex: 6,
          display: "flex", alignItems: "center", gap: "5px",
          fontFamily: MONO, fontSize: "9px", letterSpacing: "0.5px", color: "#fff",
          background: "rgba(8,7,11,0.55)", border: "1px solid rgba(255,255,255,0.14)",
          borderRadius: "999px", px: "8px", py: "4px", pointerEvents: "none",
        }}>
          <Box component="span" sx={{ color: CHERRY }}>▶</Box> your picks, as one reel
        </Box>

        {/* mute / unmute */}
        {hasVideo && (
          <Box
            component="button"
            onClick={toggleMute}
            title={muted ? "Unmute" : "Mute"}
            sx={{
              position: "absolute", top: 42, right: 12, zIndex: 8, p: 0,
              cursor: "pointer", width: 32, height: 32, borderRadius: "999px",
              border: "1px solid rgba(255,255,255,0.2)", background: "rgba(8,7,11,0.5)",
              color: "#fff", fontSize: "13px", display: "flex", alignItems: "center", justifyContent: "center",
              "&:hover": { borderColor: CHERRY },
            }}
          >
            {muted ? "🔇" : "🔊"}
          </Box>
        )}

        {/* caption + clip counter */}
        {current && (
          <Box sx={{ position: "absolute", left: 14, right: 14, bottom: 20, zIndex: 4 }}>
            <Typography sx={{
              fontFamily: F, fontWeight: 800, fontSize: "14px", lineHeight: 1.5, color: "#fff",
              display: "inline", background: "rgba(8,7,11,0.44)", borderRadius: "6px", px: "6px", py: "2px",
              boxDecorationBreak: "clone" as any, WebkitBoxDecorationBreak: "clone" as any,
            }}>
              {current.text || "Made with 🍒 CherryClipz"}
            </Typography>

            {total > 0 && (
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mt: "12px" }}>
                {showDots ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: "4px", flex: 1, flexWrap: "wrap" }}>
                    {clips.map((c, i) => (
                      <Box key={c.id} sx={{
                        width: i === idx ? 16 : 5, height: 5, borderRadius: "999px",
                        background: i === idx ? CHERRY : "rgba(255,255,255,0.4)",
                        transition: "width .25s ease, background .25s ease",
                      }} />
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ flex: 1 }} />
                )}
                <Typography sx={{ fontFamily: MONO, fontSize: "9.5px", color: "#fff", whiteSpace: "nowrap" }}>
                  clip {Math.min(idx + 1, total)}/{total}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* home indicator */}
        <Box sx={{ position: "absolute", bottom: 6, left: "50%", ml: "-44px", width: 88, height: 4, borderRadius: "999px", background: "rgba(255,255,255,0.35)", zIndex: 7, pointerEvents: "none" }} />
      </Box>
    </Box>
  );
};

export default FlagshipTikTok;
